import {
  isAnyRemoveEdit,
  isBookmarkMarkerElement,
  isInputFieldElement,
  isSuggestionMarkerElement,
  Queries,
} from '@aline/common';
import { SuggestionKind } from 'harper.js';
import type { Lint, Span, Suggestion } from 'harper.js';
import He from 'he';
import { Editor, Element, Node, Path, Range, Text } from 'slate';
import type { Point } from 'slate';

import type { SpellcheckLint } from './spellcheck-vars';

export interface ParagraphSegment {
  node: Node;
  path: Path;
  placeholderOffsets: number[];
  start: number;
  text: string;
}

/** A half-open `[start, end)` char range in the joined text occupied by a field (never spellchecked). */
export interface CharRange {
  end: number;
  start: number;
}

interface DocumentSegments {
  fieldRanges: CharRange[];
  segments: ParagraphSegment[];
  text: string;
}

const VOID_PLACEHOLDER = '\uFFFC';

/**
 * A blank line, not a single newline. Harper lints its input as markdown, where a lone newline
 * continues the paragraph.
 */
const PARAGRAPH_SEPARATOR = '\n\n';

export const DISABLED_LINT_RULES: Record<string, boolean> = {
  /** Wants `all the` in "All of the parties shall sign the amendment.", which is a preference. */
  CondenseAllThe: false,
  /**
   * Wants the singular outside a court case, but contracts use "damages" in its legal sense and as a
   * verb: "Any act that damages the reputation of the company is prohibited."
   */
  Damages: false,
  /**
   * Wants a comma after an opener that is not always a marker: "Indeed the vendor must sign the
   * amendment before Friday.", and "Notwithstanding the foregoing, …", where it is a preposition.
   */
  DiscourseMarkers: false,
  /** The hyphen in "The re-sale of the goods is prohibited." is deliberate in a contract. */
  DisjointPrefixes: false,
  /** Reads a capitalised defined term as a verb: "relating to Approved Goods". */
  InflectedVerbAfterTo: false,
  /** Sentence length is not a defect in legal drafting. */
  LongSentences: false,
  /**
   * Reads a word used as an adjective, or one it only knows as a mass noun, as the noun itself:
   * "Every message must include a functioning unsubscribe link."
   */
  MassNouns: false,
  /** Fires on plain sentences such as "The teams are separate entities." */
  MissingPreposition: false,
  /** Wants `to` inside a defined term: `"Deliveries" means inbound courier deliveries`. */
  MissingTo: false,
  /**
   * Takes the same sentences `WillNonLemma` misreads and offers a worse fix: "I will be ping you
   * later." Off with it, so a verb Harper only knows as a noun is left alone.
   */
  ModalBeAdjective: false,
  /** Two spaces between sentences is a house style. */
  NoFrenchSpaces: false,
  /**
   * Takes "effect" for a noun only, but contracts use the verb, meaning "to bring about": "The
   * parties shall take all steps to effect the transfer." Its other catches are rare in contracts.
   */
  NounVerbConfusion: false,
  /** Fires on phone numbers such as `+1 (212) 555-1234`, where an en dash is wrong. */
  NumericRangeEnDash: false,
  /** Whether to use one is a house style. `NoOxfordComma` is off in Harper's defaults already. */
  OxfordComma: false,
  /**
   * Misreads the subject of a following clause: "He withdrew his offer and coldly said she is
   * tolerable."
   */
  PronounVerbAgreement: false,
  /**
   * Takes a subject pronoun for an object one: "The tenants were fortunate, for they had the
   * advantage of an early notice."
   */
  TheyToThem: false,
  /**
   * Wants a base-form verb after "will", but Harper knows some verbs only as nouns: "I will ping
   * you later."
   */
  WillNonLemma: false,
};

const CONTEXT_BEFORE_CHARS = 45;
const CONTEXT_AFTER_CHARS = 60;
const MAX_SUGGESTIONS = 4;

const SPACES_ONLY = /^ +$/;

/** The text Harper lints, one paragraph per block, separated by `PARAGRAPH_SEPARATOR`. */
export function getDocumentSegments(editor: Editor): DocumentSegments {
  const segments: ParagraphSegment[] = [];
  const fieldRanges: CharRange[] = [];
  let start = 0;
  for (const [node, path] of Queries.getParagraphsInActiveRoots(editor)) {
    // The deletion can sit on a container above the paragraph, which this query still returns.
    if (hasRemovedAncestor(editor, path)) continue;

    const parts: string[] = [];
    const placeholderOffsets: number[] = [];
    const localFieldRanges: CharRange[] = [];
    let local = 0;

    /**
     * A field speaks for itself and a removed node has nothing left to say, so neither is walked
     * into. A checkbox sits inside its option field, so this has to reach descendants rather than
     * direct children, or the words either side of it run together.
     */
    const standsAlone = (child: Node): boolean =>
      isRemoved(child) ||
      (Element.isElement(child) && (Editor.isVoid(editor, child) || isInputFieldElement(child)));

    /**
     * Document order, which is the order `offsetToPoint` mirrors to map an offset back to a
     * position.
     */
    for (const [child] of Node.descendants(node, {
      pass: ([descendant]) => standsAlone(descendant),
    })) {
      // A removed node never reaches the reader, so it takes up no room in the text either.
      if (isRemoved(child)) continue;

      if (Element.isElement(child) && standsAlone(child)) {
        const voidText = getLintableText(child);

        // A void's text is its label, the field's own words rather than the user's.
        if (voidText) {
          localFieldRanges.push({ end: local + voidText.length, start: local });
          parts.push(voidText);
          local += voidText.length;
          continue;
        }

        /**
         * Markers are zero width and can sit inside a word, where a stand-in would split it and
         * invent a misspelling. Everything else takes up room, so it gets one to keep the words
         * either side apart.
         */
        if (isBookmarkMarkerElement(child) || isSuggestionMarkerElement(child)) continue;

        placeholderOffsets.push(local);
        localFieldRanges.push({ end: local + 1, start: local });
        parts.push(VOID_PLACEHOLDER);
        local += 1;
        continue;
      }

      if (Text.isText(child)) {
        parts.push(child.text);
        local += child.text.length;
      }
    }

    const text = parts.join('');
    segments.push({ node, path, placeholderOffsets, start, text });
    for (const { end, start: fieldStart } of localFieldRanges) {
      fieldRanges.push({ end: start + end, start: start + fieldStart });
    }

    start += text.length + PARAGRAPH_SEPARATOR.length;
  }

  return {
    fieldRanges,
    segments,
    text: segments.map(segment => segment.text).join(PARAGRAPH_SEPARATOR),
  };
}

export interface HarperLintLike extends Pick<
  Lint,
  'get_problem_text' | 'lint_kind_pretty' | 'message_html'
> {
  span(): Pick<Span, 'end' | 'start'>;
  suggestions(): Pick<Suggestion, 'get_replacement_text' | 'kind'>[];
}

/** Lints whose span no longer resolves to a Slate position, or that touch a field, are dropped. */
export function buildSpellcheckLints<T extends HarperLintLike>(
  lints: T[],
  segments: ParagraphSegment[],
  fieldRanges: CharRange[]
): { harperLint: T; lint: SpellcheckLint }[] {
  return lints.flatMap(lint => {
    const span = lint.span();
    // Drop any lint that intersects with a field element
    if (fieldRanges.some(field => span.start < field.end && span.end > field.start)) return [];

    const problem = lint.get_problem_text();
    if (isLayoutSpacing(problem)) return [];

    const startSegment = findSegment(segments, span.start);
    const endSegment = findSegment(segments, span.end);
    if (!startSegment || !endSegment) return [];

    const range: Range = {
      anchor: offsetToPoint(startSegment, span.start, 'start'),
      focus: offsetToPoint(endSegment, span.end, 'end'),
    };

    const kind = lint.lint_kind_pretty();
    const localStart = span.start - startSegment.start;
    const localEnd = span.end - startSegment.start;
    const contextBefore = startSegment.text
      .slice(Math.max(0, localStart - CONTEXT_BEFORE_CHARS), localStart)
      .replaceAll(VOID_PLACEHOLDER, '');
    const contextAfter =
      startSegment === endSegment
        ? startSegment.text
            .slice(localEnd, localEnd + CONTEXT_AFTER_CHARS)
            .replaceAll(VOID_PLACEHOLDER, '')
        : '';

    return [
      {
        harperLint: lint,
        lint: {
          contextAfter,
          contextBefore,
          kind,
          message: getMessageText(lint.message_html()),
          problem,
          range,
          suggestions: lint
            .suggestions()
            .slice(0, MAX_SUGGESTIONS)
            .map(suggestion => ({
              kind: suggestion.kind(),
              text: suggestion.get_replacement_text(),
            })),
        },
      },
    ];
  });
}

export function getSuggestionTargetRange(range: Range, kind: SuggestionKind): Range {
  if (kind === SuggestionKind.InsertAfter) {
    const end = Range.end(range);
    return { anchor: end, focus: end };
  }
  return range;
}

export function isRangeLive(editor: Editor, range: Range): boolean {
  return Editor.hasPath(editor, range.anchor.path) && Editor.hasPath(editor, range.focus.path);
}

/**
 * The range that still sits on the flagged text. A pass stores a Slate range ref; tests that
 * never run one keep only the snapshot.
 */
export function getLiveSpellcheckRange(lint: SpellcheckLint): Range | null {
  if (lint.rangeRef) return lint.rangeRef.current;
  return lint.range;
}

export function isLintRangeIntact(editor: Editor, lint: SpellcheckLint): boolean {
  const range = getLiveSpellcheckRange(lint);
  if (!range || !isRangeLive(editor, range)) return false;
  return getLintableRangeText(editor, range) === lint.problem;
}

/**
 * What a suggestion button shows. A run of spaces draws as nothing, leaving a narrow blank button
 * that gives the reader no idea what pressing it does, so it is named instead.
 */
export function getSuggestionLabel(text: string): string {
  if (text === '') return 'Delete';
  if (!SPACES_ONLY.test(text)) return text;
  return text.length === 1 ? 'Space' : `${text.length} spaces`;
}

/**
 * Harper's message arrives as a small HTML fragment and is only ever shown as text, so this trims
 * markup for display rather than guarding anything. A single pass already leaves no tags behind.
 */
function getMessageText(html: string): string {
  let text = html;
  let previous: string;
  do {
    previous = text;
    text = text.replaceAll(/<code[^>]*>([^<]*)<\/code>/g, '“$1”').replaceAll(/<[^>]*>/g, '');
  } while (text !== previous);
  return He.decode(text).trim();
}

/**
 * Two spaces is usually a slip worth flagging, which is why Harper's `Spaces` rule stays on. A
 * longer run is how people lay out a signature block, and the same rule reports it, so it is
 * dropped here instead.
 */
function isLayoutSpacing(problem: string): boolean {
  return problem.length > 2 && SPACES_ONLY.test(problem);
}

/**
 * The range's text as Harper saw it, leaving out the deleted runs it never linted. A problem
 * fused across a pending deletion sits in two separate nodes, so comparing the whole range would
 * pick up the deleted text between them and never match.
 */
function getLintableRangeText(editor: Editor, range: Range): string {
  const [rangeStart, rangeEnd] = Range.edges(range);
  let text = '';
  for (const [node, path] of Editor.nodes(editor, {
    at: range,
    match: Text.isText,
    pass: ([descendant]) => isRemoved(descendant),
  })) {
    if (isRemoved(node)) continue;
    const start = Path.equals(path, rangeStart.path) ? rangeStart.offset : 0;
    const end = Path.equals(path, rangeEnd.path) ? rangeEnd.offset : node.text.length;
    text += node.text.slice(start, end);
  }
  return text;
}

function findSegment(segments: ParagraphSegment[], offset: number): ParagraphSegment | null {
  return (
    segments.find(
      segment => offset >= segment.start && offset <= segment.start + segment.text.length
    ) ?? null
  );
}

function offsetToPoint(
  segment: ParagraphSegment,
  globalOffset: number,
  edge: 'end' | 'start'
): Point {
  const offsetInText = globalOffset - segment.start;
  const placeholdersBefore = segment.placeholderOffsets.filter(
    offset => offset < offsetInText
  ).length;
  let local = offsetInText - placeholdersBefore;
  let lastPoint: Point = { offset: 0, path: segment.path };
  for (const [textNode, relativePath] of Node.texts(segment.node, {
    pass: ([descendant]) => isRemoved(descendant),
  })) {
    if (isRemoved(textNode)) continue;
    const path = [...segment.path, ...relativePath];
    const endsHere = edge === 'end' ? local <= textNode.text.length : local < textNode.text.length;
    if (endsHere) return { offset: local, path };
    local -= textNode.text.length;
    lastPoint = { offset: textNode.text.length, path };
  }
  return lastPoint;
}

/**
 * A table cell or other non-void container can carry the deletion while the words inside it carry
 * none, so the paragraph itself is clean and only an ancestor says it is gone.
 */
function hasRemovedAncestor(editor: Editor, path: Path): boolean {
  for (const [ancestor] of Node.ancestors(editor, path)) {
    if (isRemoved(ancestor)) return true;
  }
  return false;
}

function isRemoved(node: Node): boolean {
  if (Editor.isEditor(node)) return false;
  return Boolean(node.suggestedEdits?.some(isAnyRemoveEdit));
}

function getLintableText(node: Node): string {
  let text = '';
  for (const [textNode] of Node.texts(node, { pass: ([descendant]) => isRemoved(descendant) })) {
    if (isRemoved(textNode)) continue;
    text += textNode.text;
  }
  return text;
}
